import { cva, type VariantProps } from 'class-variance-authority'
import type { LucideIcon } from 'lucide-react'
import { ChevronRight } from 'lucide-react'

import { cn } from '@/customer/lib/utils'

/**
 * Big rectangular CTA on portal surfaces. Two visual variants:
 *
 * - `tile` (default): muted card with a coloured icon tile on the
 *   left, uppercase Barlow label, chevron on the right. Used for the
 *   portal home action grid where several CTAs sit side by side and
 *   the tile colour differentiates them.
 * - `solid`: whole card is filled with the `tone` colour, icon is
 *   inline with no tile. Used for standalone primary CTAs like
 *   "Add another pet" on the pets index, where the CTA wants to
 *   read as one confident coloured block rather than a decorated row.
 *
 * Renders as a `<button>` or `<a>` depending on whether `onClick` or
 * `href` is provided.
 */

const iconTile = cva(
  'flex size-14 shrink-0 items-center justify-center rounded-xl text-foreground',
  {
    variants: {
      tone: {
        yellow: 'bg-yellow',
        pink: 'bg-pink',
        blue: 'bg-blue text-background',
        'baby-blue': 'bg-baby-blue',
        green: 'bg-green text-background',
        orange: 'bg-orange',
      },
    },
    defaultVariants: { tone: 'yellow' },
  }
)

const solidSurface = cva(
  'flex w-full items-center gap-4 rounded-xl p-3 xs:p-4 text-foreground text-left transition-colors',
  {
    variants: {
      tone: {
        yellow: 'bg-yellow hover:bg-yellow/90',
        pink: 'bg-pink hover:bg-pink/90',
        blue: 'bg-blue text-background hover:bg-blue/90',
        'baby-blue': 'bg-baby-blue hover:bg-baby-blue/90',
        green: 'bg-green text-background hover:bg-green/90',
        orange: 'bg-orange hover:bg-orange/90',
      },
    },
    defaultVariants: { tone: 'yellow' },
  }
)

export type ActionCardTone = NonNullable<VariantProps<typeof iconTile>['tone']>
export type ActionCardVariant = 'tile' | 'solid'

type CommonProps = {
  icon: LucideIcon
  label: string
  tone?: ActionCardTone
  variant?: ActionCardVariant
  className?: string
  /**
   * When true, renders a muted, non-interactive card. Used to gate
   * actions when prerequisites aren't met without hiding the
   * affordance entirely.
   */
  disabled?: boolean
  /**
   * Optional handler fired when a `disabled` card is clicked. When
   * provided, the card is rendered `aria-disabled` (rather than
   * natively `disabled`) so clicks still reach it - useful for
   * cueing the member to a checklist / requirement elsewhere on the
   * page instead of leaving the click dead.
   */
  onDisabledClick?: () => void
}

export function ActionCard(
  props: CommonProps & ({ onClick: () => void; href?: never } | { href: string; onClick?: never })
) {
  const { icon: Icon, label, tone, variant = 'tile', className, disabled } = props

  const surface =
    variant === 'solid'
      ? cn(
          solidSurface({ tone }),
          'group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          className
        )
      : cn(
          'group flex w-full items-center gap-4 rounded-xl bg-muted p-3 xs:p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-neutral-200',
          className
        )

  const body =
    variant === 'solid' ? (
      <>
        <Icon className="size-6 shrink-0" strokeWidth={2.25} />
        <span className="flex-1 text-button">{label}</span>
        <ChevronRight
          className="size-5 shrink-0 transition-transform group-hover:translate-x-0.5"
          aria-hidden
        />
      </>
    ) : (
      <>
        <span className={iconTile({ tone })}>
          <Icon className="size-6" strokeWidth={2.25} />
        </span>
        <span className="flex-1 text-button">{label}</span>
        <ChevronRight
          className="size-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
          aria-hidden
        />
      </>
    )

  if (disabled) {
    // `aria-disabled` (rather than native `disabled`) when the caller
    // wants to react to the click - lets us cue the blocker without
    // making the card silently unresponsive. Falls back to native
    // `disabled` when no click handler is provided so keyboard/screen
    // reader behaviour matches a plain inert button.
    const clickable = !!props.onDisabledClick
    return (
      <button
        type="button"
        disabled={!clickable}
        aria-disabled
        onClick={clickable ? props.onDisabledClick : undefined}
        className={surface}
      >
        {body}
      </button>
    )
  }

  if ('href' in props && props.href) {
    // External links (WhatsApp, mailto:) - the portal only issues
    // these; internal navigation goes via router `<Link>` at the
    // callsite if/when we need it.
    const external = /^https?:/.test(props.href)
    return (
      <a
        href={props.href}
        className={surface}
        target={external ? '_blank' : undefined}
        rel={external ? 'noreferrer noopener' : undefined}
      >
        {body}
      </a>
    )
  }

  return (
    <button type="button" onClick={props.onClick} className={surface}>
      {body}
    </button>
  )
}
